import { Link, useLocation } from "wouter";
import { useState, useRef, useEffect } from "react";
import { DnaGlyph } from "@/components/DnaGlyph";
import { getSession } from "@/lib/session";

function readMyPipelineScore(): number | null {
  try {
    const raw = localStorage.getItem("aip:pipeline-dna:me-score");
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Top nav — V3.1 canonical nav structure.
 *
 * Base items visible to all roles.
 * Operator extras (manager / executive / isDualRole) append additional
 * items matching the merged-canonical nav list from overnight-break-list.md.
 *
 * URL corrections applied 2026-05-20:
 *   - Opportunity DNA: /helix -> /dna -> /opportunity-dna  (canonical; aliases redirect)
 *   - Activity Audit:  /audit -> /activity/audit  (correct V3.1 route)
 * W2-Alias (2026-05-27): /dna updated to canonical /opportunity-dna
 * W37-A (2026-05-27): Cadence link added for bdr and ae roles (first in role-specific set)
 */

/**
 * BDR-specific nav extras — Cadence is the BDR home base for the day.
 * Shown only when session.role === "bdr" or "ae".
 */
/**
 * W66.B: Workspace — leftmost nav item after Home for all roles.
 */
const WORKSPACE_NAV_ITEM = { href: "/workspace", label: "Workspace", matches: (l: string) => l === "/workspace" };

const BDR_EXTRA_ITEMS: { href: string; label: string; matches: (loc: string) => boolean }[] = [
  { href: "/cadence", label: "Cadence", matches: (l) => l.startsWith("/cadence") },
];

const AE_EXTRA_ITEMS: { href: string; label: string; matches: (loc: string) => boolean }[] = [
  { href: "/cadence", label: "Cadence", matches: (l) => l.startsWith("/cadence") },
];

const ITEMS: { href: string; label: string; matches: (loc: string) => boolean; overflow?: boolean }[] = [
  { href: "/accounts",         label: "Accounts",        matches: (l) => l === "/" || l === "/accounts" || l.startsWith("/account/") },
  { href: "/opportunities",    label: "Opportunities",   matches: (l) => l === "/opportunities" || l.startsWith("/opportunity") },
  // W46 — chart-first dashboards (visible to all roles)
  { href: "/dashboards/charts", label: "Dashboards",     matches: (l) => l === "/dashboards/charts" || l.startsWith("/dashboards/pipeline-forecast") || l.startsWith("/dashboards/account-health") || l === "/dashboards/marketing" },
  // W45.1: Command View — money-shot surface
  { href: "/command-view",     label: "Command View",    matches: (l) => l.startsWith("/command-view") || l.includes("/command-view") },
  { href: "/opportunity-dna",  label: "Opportunity DNA", matches: (l) => l.startsWith("/opportunity-dna") || l.startsWith("/dna") || (l.startsWith("/helix") && !l.startsWith("/helix-demo")) },
  { href: "/flowcharts",    label: "Flowcharts",      matches: (l) => l.startsWith("/flowcharts") },
  { href: "/templates",     label: "Templates",       matches: (l) => l.startsWith("/templates") },
  // W66.W6.2 — Sales Plan (Top-10 Account workbook). Visible to all roles. /sales-plan/quota stays manager+executive only.
  { href: "/sales-plan",    label: "Sales Plan",      matches: (l) => l === "/sales-plan" || l.startsWith("/sales-plan/") },
  // W66.D: Methodology, Question Bank, Manuals promoted to sidebar — removed from TopNav.
];

/**
 * Operator extras visible only to executive / manager (and isDualRole).
 * Matches the merged-canonical nav list.
 */
const NAVMAX_EXTRA: { href: string; label: string; matches: (loc: string) => boolean }[] = [
  { href: "/coaching",              label: "Coaching",        matches: (l) => l.startsWith("/coaching") },
  { href: "/reporting",             label: "Reporting",       matches: (l) => l.startsWith("/reporting") },
  // W46 — chart-first dashboards alongside /reports (also in ITEMS for all roles)
  { href: "/reports",               label: "Reports",         matches: (l) => l.startsWith("/reports") },
  { href: "/marketing",             label: "Marketing",       matches: (l) => l.startsWith("/marketing") },
  // W66.H — Funnel: manager/exec only in TopNav; AE/BDR access via /marketing banner
  { href: "/funnel",                label: "Funnel",          matches: (l) => l.startsWith("/funnel") },
  { href: "/activity/audit",        label: "Activity Audit",  matches: (l) => l.startsWith("/activity/audit") || l.startsWith("/audit") },
  { href: "/settings",              label: "Settings",        matches: (l) => l.startsWith("/settings") },
  { href: "/admin/activity-policy", label: "Activity Policy", matches: (l) => l.startsWith("/admin/activity-policy") },
];

export function TopNav() {
  const [loc] = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const [myScore, setMyScore] = useState<number | null>(() => readMyPipelineScore());

  // Operator tier: executive + manager (and isDualRole) get navmax.
  const session = getSession();
  const isOperator = session
    ? session.isDualRole ||
      session.role === "executive" ||
      session.role === "manager"
    : false;

  // W37-A: BDR and AE roles get Cadence as first nav item.
  const isBdr = session?.role === "bdr";
  const isAe  = session?.role === "ae";

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === "aip:pipeline-dna:me-score") setMyScore(readMyPipelineScore());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const myDnaActive = loc.startsWith("/pipeline-dna/me");

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [moreOpen]);

  const primary = ITEMS.filter((it) => !it.overflow);
  const secondary = ITEMS.filter((it) => it.overflow);
  const hasActiveOverflow = secondary.some((it) => it.matches(loc));

  // W37-A: Prepend Cadence link for BDR and AE — cadence is their home base.
  const roleExtras = isBdr
    ? BDR_EXTRA_ITEMS
    : isAe
    ? AE_EXTRA_ITEMS
    : [];
  // W66.B: Workspace is leftmost for all roles
  const allNavItems = isOperator
    ? [WORKSPACE_NAV_ITEM, ...primary, ...NAVMAX_EXTRA]
    : [WORKSPACE_NAV_ITEM, ...roleExtras, ...primary];

  return (
    <nav
      className={`flex items-center gap-0.5 text-sm overflow-x-auto -mx-2 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden flex-wrap${isOperator ? " navmax-after" : ""}`}
      aria-label="Primary"
    >
      {allNavItems.map((it) => {
        const active = it.matches(loc);
        return (
          <Link
            key={it.href}
            href={it.href}
            className={`whitespace-nowrap px-3 py-1.5 rounded-md text-[13px] transition-colors ${
              active
                ? "bg-[hsl(var(--surface-2))] text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/60"
            }`}
          >
            {it.label}
          </Link>
        );
      })}

      {/* Overflow group */}
      <div className="relative flex items-center" ref={moreRef}>
        {secondary.map((it) => {
          const active = it.matches(loc);
          return (
            <Link
              key={it.href}
              href={it.href}
              className={`hidden md:inline-flex whitespace-nowrap px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                active
                  ? "bg-[hsl(var(--surface-2))] text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/60"
              }`}
            >
              {it.label}
            </Link>
          );
        })}

        {secondary.length > 0 && (
          <div className="md:hidden relative">
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                hasActiveOverflow || moreOpen
                  ? "bg-[hsl(var(--surface-2))] text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/60"
              }`}
              aria-haspopup="true"
              aria-expanded={moreOpen}
            >
              More
            </button>
            {moreOpen && (
              <div className="absolute top-full right-0 mt-1 z-50 min-w-[140px] bg-[hsl(var(--surface))] border border-border rounded-md shadow-md py-1">
                {secondary.map((it) => {
                  const active = it.matches(loc);
                  return (
                    <Link
                      key={it.href}
                      href={it.href}
                      onClick={() => setMoreOpen(false)}
                      className={`block px-4 py-2 text-[13px] transition-colors ${
                        active
                          ? "bg-[hsl(var(--surface-2))] text-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/60"
                      }`}
                    >
                      {it.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Website link */}
      <a
        href="https://mi6group.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open Mi6 Group website in a new tab"
        className="ml-auto inline-flex items-center gap-1 whitespace-nowrap px-3 py-1.5 rounded-md text-[13px] transition-colors text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/60"
      >
        <span>Website</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M7 17L17 7M9 7h8v8" />
        </svg>
      </a>

      {/* My DNA */}
      <Link
        href="/pipeline-dna/me"
        aria-label="My DNA — open my pipeline DNA"
        className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-md text-[13px] transition-colors focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40 ${
          myDnaActive
            ? "bg-[hsl(var(--surface-2))] text-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/60"
        }`}
      >
        <DnaGlyph score={myScore} size={20} ariaLabel="My pipeline DNA" />
        <span>My DNA</span>
      </Link>
    </nav>
  );
}
